import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import { newsService, NewsArticle } from '../services/newsService';
import SectionTitle from './shared/SectionTitle';

const NewsCarouselContainer = styled.section`
  width: 100%;
  padding: 64px 5%;
  background: ${theme.colors.outchou_blue};

  @media (max-width: 768px) {
    padding: 48px 4%;
  }

  @media (max-width: 480px) {
    padding: 32px 3%;
  }
`;

const NewsCarouselContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const NewsSectionTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  color: ${theme.colors.white};
  margin: 0;
  text-align: justify;

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
    text-align: left;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    line-height: 36px;
    text-align: left;
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  overflow: visible;
  position: relative;
  padding: 0 48px;

  @media (max-width: 768px) {
    padding: 0 24px;
  }

  @media (max-width: 480px) {
    padding: 0 16px;
  }
`;

const NewsCarouselWrapper = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  padding: 20px 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const NewsCard = styled.article`
  width: 320px;
  min-width: 320px;
  background: transparent;
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
  transition: transform 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: scale(1.01);
  }

  @media (max-width: 768px) {
    width: 280px;
    min-width: 280px;
  }

  @media (max-width: 480px) {
    width: 240px;
    min-width: 240px;
  }
`;

const NewsImage = styled.div<{ imageUrl?: string }>`
  width: 100%;
  height: 400px;
  background-image: url(${props => props.imageUrl || '/shared_assets/images/news-placeholder.jpg'});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.2) 100%);
    z-index: 1;
  }

  @media (max-width: 768px) {
    height: 350px;
    padding: 24px;
  }

  @media (max-width: 480px) {
    height: 300px;
    padding: 20px;
  }
`;

const NewsCardContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NewsCardTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 20px;
  color: white;
  margin: 0 0 8px 0;
  text-align: left;
  letter-spacing: -0.3px;
  line-height: 1.3;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const NewsCardDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  text-align: left;
  line-height: 1.5;
  margin: 0 0 12px 0;
  font-weight: 300;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const NewsCardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const NewsSource = styled.span`
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  color: white;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 10px;
  border-radius: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 14px;
    padding: 3px 8px;
  }
`;

const NewsDate = styled.span`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 14px;
  }
`;

const CarouselControls = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  z-index: 2;
`;

const CarouselButton = styled.button`
  width: 48px;
  height: 48px;
  background: ${theme.colors.white};
  border: none;
  border-radius: 50%;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  pointer-events: auto;

  &:hover {
    background: ${theme.colors.outchou_blue};
    transform: scale(1.1);
    
    img {
      filter: brightness(0) invert(1);
    }
  }

  img {
    width: 20px;
    height: 20px;
    transition: filter 0.3s ease;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    img {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;

    img {
      width: 14px;
      height: 14px;
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
  font-family: ${theme.fonts.body};
  font-size: 18px;
  color: ${theme.colors.gray};
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 0;
  text-align: center;
`;

const ErrorMessage = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 18px;
  color: ${theme.colors.gray};
  margin: 0;
`;

const RetryButton = styled.button`
  background: ${theme.colors.outchou_green};
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 16px;
  color: ${theme.colors.white};
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${theme.colors.outchou_green_hover};
  }
`;

interface NewsCarouselProps {
  maxArticles?: number;
}

const NewsCarousel: React.FC<NewsCarouselProps> = ({ maxArticles = 12 }) => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isBackendHealthy = await newsService.checkBackendHealth();
      if (!isBackendHealthy) {
        throw new Error('Le serveur backend n\'est pas accessible. Veuillez vérifier que le serveur Flask est en cours d\'exécution.');
      }
      
      const response = await newsService.getAlgeriaHeadlines(maxArticles);
      
      if (response.status === 'success') {
        setArticles(response.articles || []);
      } else {
        throw new Error(response.message || 'Erreur lors de la récupération des actualités');
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err instanceof Error ? err.message : 'Une erreur inattendue s\'est produite');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [maxArticles]);

  const scrollCarousel = (direction: 'left' | 'right') => {
    const carousel = document.querySelector('.news-carousel');
    if (carousel) {
      const scrollAmount = 400;
      const currentScroll = carousel.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      carousel.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  const handleArticleClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <NewsCarouselContainer>
        <NewsCarouselContent>
          <NewsSectionTitle>Actualités culturelles d'Algérie</NewsSectionTitle>
          <LoadingState>Chargement des actualités...</LoadingState>
        </NewsCarouselContent>
      </NewsCarouselContainer>
    );
  }

  if (error) {
    return (
      <NewsCarouselContainer>
        <NewsCarouselContent>
          <NewsSectionTitle>Actualités culturelles d'Algérie</NewsSectionTitle>
          <ErrorState>
            <ErrorMessage>{error}</ErrorMessage>
            <RetryButton onClick={fetchNews}>Réessayer</RetryButton>
          </ErrorState>
        </NewsCarouselContent>
      </NewsCarouselContainer>
    );
  }

  if (articles.length === 0) {
    return (
      <NewsCarouselContainer>
        <NewsCarouselContent>
          <NewsSectionTitle>Actualités culturelles d'Algérie</NewsSectionTitle>
          <LoadingState>Aucune actualité disponible pour le moment.</LoadingState>
        </NewsCarouselContent>
      </NewsCarouselContainer>
    );
  }

  return (
    <NewsCarouselContainer>
      <NewsCarouselContent>
        <NewsSectionTitle>Actualités culturelles d'Algérie</NewsSectionTitle>
        <CarouselContainer>
          <NewsCarouselWrapper className="news-carousel">
            {articles.map((article) => (
              <NewsCard 
                key={article.id} 
                onClick={() => handleArticleClick(article.url)}
              >
                <NewsImage imageUrl={article.imageUrl}>
                  <NewsCardContent>
                    <NewsCardTitle>{article.title}</NewsCardTitle>
                    <NewsCardDescription>
                      {newsService.truncateText(article.description, 100)}
                    </NewsCardDescription>
                    <NewsCardMeta>
                      <NewsSource>{article.source}</NewsSource>
                      <NewsDate>
                        {newsService.formatDate(article.publishedAt)}
                      </NewsDate>
                    </NewsCardMeta>
                  </NewsCardContent>
                </NewsImage>
              </NewsCard>
            ))}
          </NewsCarouselWrapper>
          <CarouselControls>
            <CarouselButton onClick={() => scrollCarousel('left')}>
              <img src="/shared_assets/images/carousel_left.png" alt="Previous" />
            </CarouselButton>
            <CarouselButton onClick={() => scrollCarousel('right')}>
              <img src="/shared_assets/images/carousel_right.png" alt="Next" />
            </CarouselButton>
          </CarouselControls>
        </CarouselContainer>
      </NewsCarouselContent>
    </NewsCarouselContainer>
  );
};

export default NewsCarousel; 